import React from 'react';
import { MapPin, Zap, Calendar } from 'lucide-react';

/**
 * Компонент раздела с параметрами тарифа
 */
const TariffParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  russianRegions,
  tariffTypes,
  meterTypes
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Zap className="w-5 h-5 mr-2 text-yellow-500" />
        Параметры тарифа и учета
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Регион */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-yellow-500" />
            Регион России
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.region ? 'input-error' : ''}`}
          >
            {russianRegions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.region}
            </p>
          )}
        </div>

        {/* Дата расчета */}
        <div>
          <label htmlFor="calculationDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
            Дата расчета
          </label>
          <input
            type="date"
            id="calculationDate"
            name="calculationDate"
            value={formData.calculationDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.calculationDate ? 'input-error' : ''}`}
          />
          {errors.calculationDate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.calculationDate}
            </p>
          )}
        </div>

        {/* Тип тарифа */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип тарифа
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {tariffTypes.map((tariff) => (
              <label key={tariff.id} className="flex flex-col p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start mb-1">
                  <input
                    type="radio"
                    name="tariffType"
                    value={tariff.id}
                    checked={formData.tariffType === tariff.id}
                    onChange={handleInputChange}
                    className="form-radio mt-0.5"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {tariff.name}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                  {tariff.description}
                </p>
              </label>
            ))}
          </div>
          {errors.tariffType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.tariffType}
            </p>
          )}
        </div>

        {/* Тип счетчика */}
        <div className="col-span-full mt-3">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип счетчика
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {meterTypes.map((meter) => (
              <label 
                key={meter.id} 
                className={`flex flex-col p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  (formData.tariffType !== 'single' && meter.id === 'single') ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start mb-1">
                  <input
                    type="radio"
                    name="meterType"
                    value={meter.id}
                    checked={formData.meterType === meter.id}
                    onChange={handleInputChange}
                    disabled={formData.tariffType !== 'single' && meter.id === 'single'}
                    className="form-radio mt-0.5"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {meter.name}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                  {meter.description}
                </p>
              </label>
            ))}
          </div>
          {errors.meterType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.meterType}
            </p>
          )}
        </div>

        {/* Метод расчета */}
        <div className="col-span-full mt-3">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Метод расчета потребления
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-start mb-1">
                <input
                  type="radio"
                  name="consumptionType"
                  value="manual"
                  checked={formData.consumptionType === 'manual'}
                  onChange={handleInputChange}
                  className="form-radio mt-0.5"
                />
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ручной ввод потребления
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                Указать потребление за месяц вручную в киловатт-часах (кВт⋅ч)
              </p>
            </label>
            <label className="flex flex-col p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-start mb-1">
                <input
                  type="radio"
                  name="consumptionType"
                  value="appliances"
                  checked={formData.consumptionType === 'appliances'}
                  onChange={handleInputChange}
                  className="form-radio mt-0.5"
                />
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Расчет по электроприборам
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                Рассчитать потребление по используемым бытовым приборам
              </p>
            </label>
          </div>
          {errors.consumptionType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.consumptionType}
            </p>
          )}
        </div>
      </div>

      {/* Информация о тарифах */}
      {formData.region && (
        <div className="mt-5 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800 text-xs">
          <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Текущие тарифы для региона: {russianRegions.find(r => r.id === formData.region)?.name}</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Одноставочный:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.singleRate} ₽/кВт⋅ч</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Дневной:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.dayRate} ₽/кВт⋅ч</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Ночной:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.nightRate} ₽/кВт⋅ч</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Пиковый:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.peakRate} ₽/кВт⋅ч</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Полупиковый:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.semipeakRate} ₽/кВт⋅ч</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400 font-medium">Внепиковый:</span>
              <span className="text-blue-600 dark:text-blue-400 ml-1">{russianRegions.find(r => r.id === formData.region)?.offpeakRate} ₽/кВт⋅ч</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TariffParametersSection;